import { useEffect, useMemo, useRef, useState } from 'react';
import './AnalysisDropdown.css';

const normalizeSearchText = (value) => String(value ?? '')
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim();

export default function SearchableDropdown({
  id,
  value,
  options = [],
  placeholder = 'Seçin',
  searchPlaceholder = 'Ders adı veya koduyla ara…',
  emptyText = 'Eşleşen kayıt bulunamadı.',
  disabled = false,
  flowMenu = false,
  onChange,
  ariaLabel = 'Seçim',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const selectedOption = options.find(option => String(option.value) === String(value));

  const filteredOptions = useMemo(() => {
    const query = normalizeSearchText(search);
    if (!query) return options;
    return options.filter(option => normalizeSearchText(
      `${option.label || ''} ${option.searchText || ''}`
    ).includes(query));
  }, [options, search]);

  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const choose = (nextValue) => {
    onChange?.(String(nextValue));
    setOpen(false);
    setSearch('');
  };

  return (
    <div className={`analysis-searchable${flowMenu ? ' analysis-searchable--flow' : ''}`} ref={rootRef}>
      <button
        id={id}
        type="button"
        className={`analysis-searchable__trigger${open ? ' is-open' : ''}`}
        onClick={() => !disabled && setOpen(current => !current)}
        onKeyDown={(event) => {
          if (!disabled && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="analysis-searchable__value">{selectedOption?.label || placeholder}</span>
        {selectedOption?.badge && <span className="analysis-searchable__badge">{selectedOption.badge}</span>}
        <span className="analysis-searchable__chevron" aria-hidden="true">⌄</span>
      </button>

      {open && !disabled && (
        <div className="analysis-searchable__menu">
          <div className="analysis-searchable__search-wrap">
            <span className="analysis-searchable__search-icon" aria-hidden="true">⌕</span>
            <input
              ref={searchRef}
              type="search"
              className="analysis-searchable__search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && filteredOptions.length === 1) {
                  event.preventDefault();
                  choose(filteredOptions[0].value);
                }
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>
          <div className="analysis-searchable__options" role="listbox" aria-label={ariaLabel}>
            {!search && (
              <button
                type="button"
                className={`analysis-searchable__option${value ? '' : ' is-selected'}`}
                onClick={() => choose('')}
                role="option"
                aria-selected={!value}
              >
                <span className="analysis-searchable__option-name">{placeholder}</span>
              </button>
            )}
            {filteredOptions.map(option => (
              <button
                type="button"
                key={option.value}
                className={`analysis-searchable__option${String(value) === String(option.value) ? ' is-selected' : ''}`}
                onClick={() => choose(option.value)}
                role="option"
                aria-selected={String(value) === String(option.value)}
              >
                <span className="analysis-searchable__option-name">{option.label}</span>
                {option.badge && <span className="analysis-searchable__badge">{option.badge}</span>}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="analysis-searchable__empty">{emptyText}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
